import { Box, Typography, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import type { SaveStatus as SaveStatusType } from '../../hooks/useAutoSave';

interface SaveStatusProps {
  status: SaveStatusType;
  lastSavedAt: Date | null;
}

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  if (seconds < 10) {
    return 'just now';
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Component to display save status with icon and message
 */
export const SaveStatus = ({ status, lastSavedAt }: SaveStatusProps) => {
  if (status === 'idle') {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.5,
        minHeight: '24px',
      }}
    >
      {status === 'saving' && (
        <>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Saving...
          </Typography>
        </>
      )}

      {status === 'saved' && lastSavedAt && (
        <>
          <CheckCircleIcon
            sx={{ fontSize: 14, color: 'success.main' }}
          />
          <Typography variant="caption" color="text.secondary">
            Saved {formatTimeAgo(lastSavedAt)}
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <ErrorIcon
            sx={{ fontSize: 14, color: 'error.main' }}
          />
          <Typography variant="caption" color="error.main">
            Failed to save
          </Typography>
        </>
      )}
    </Box>
  );
};
